import React from "react";
import { FiArrowRight, FiEdit2, FiTrash2 } from "react-icons/fi";

const ArrowRightIcon = FiArrowRight as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit2 as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface TemplateCardProps {
  name: string;
  description: string;
  subject: string;
  isBuiltIn?: boolean;
  onUse: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

/**
 * TemplateCard - Displays a single email template
 * Used for both built-in and custom templates
 */
const TemplateCard: React.FC<TemplateCardProps> = ({
  name,
  description,
  subject,
  isBuiltIn = false,
  onUse,
  onEdit,
  onDelete,
}) => {
  // Handle card click - trigger onUse when clicking the card body
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('.su-template-card-actions')) {
      return;
    }
    onUse();
  };

  return (
    <div
      className="su-template-card su-template-card-clickable"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onUse();
        }
      }}
    >
      {/* Left side: Content */}
      <div className="su-template-card-content">
        <h3 className="su-template-card-title">{name}</h3>
        <p className="su-template-card-description">{description || "No description"}</p>
        <span className="su-template-card-subject">Subject: {subject}</span>
      </div>

      {/* Right side: Badge and Actions */}
      <div className="su-template-card-right">
        <span className={isBuiltIn ? "su-badge-builtin" : "su-badge-custom"}>
          {isBuiltIn ? "BUILT-IN" : "CUSTOM"}
        </span>
        <div className="su-template-card-actions">
          {/* Edit and Delete buttons for custom templates */}
          {!isBuiltIn && onEdit && (
            <button
              className="su-template-action-btn"
              onClick={onEdit}
              title="Edit template"
            >
              <EditIcon className="w-4 h-4" />
            </button>
          )}
          {!isBuiltIn && onDelete && (
            <button
              className="su-template-action-btn su-template-action-btn-delete"
              onClick={onDelete}
              title="Delete template"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <button
            className="su-template-card-use"
            onClick={onUse}
            title="Use this template"
          >
            <span>USE</span>
            <ArrowRightIcon className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;
